<template>
  <div class="row justify-content-center">
    <div class="col-md-10 col-lg-8 col-xl-7 col-xxl-6">
      <div class="login-card">

        <!-- Title -->
        <h4 class="text-center fw-bold text-dark mb-4">
          Bienvenue
        </h4>

        <!-- Tab switcher -->
        <div class="login-tabs mb-4">
          <button
            class="login-tab-btn"
            :class="{ active: activeTab === 'admin' }"
            @click="activeTab = 'admin'"
            type="button"
          >
            <i class="flaticon-enter me-1 fs-14"></i> Administrateur
          </button>
          <button
            class="login-tab-btn"
            :class="{ active: activeTab === 'staff' }"
            @click="activeTab = 'staff'"
            type="button"
          >
            <i class="flaticon-enter me-1 fs-14"></i> Staff
          </button>
        </div>

        <!-- Admin form -->
        <form v-if="activeTab === 'admin'" @submit.prevent="handleLogin">
          <div class="mb-3">
            <label class="form-label fw-semibold text-dark small">Email</label>
            <input
              v-model="email"
              type="email"
              class="form-control login-input"
              placeholder="Email"
              required
            />
          </div>

          <div class="mb-2">
            <div class="d-flex justify-content-between align-items-center">
              <label class="form-label fw-semibold text-dark small mb-0">Mot de passe</label>
              <a href="#" class="text-primary small text-decoration-none">Mot de passe Oublié?</a>
            </div>
            <div class="position-relative mt-1">
              <input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                class="form-control login-input pe-5"
                placeholder="Password"
                required
              />
              <button
                type="button"
                class="btn-eye"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'flaticon-eye-1' : 'flaticon-eye'" class="fs-14"></i>
              </button>
            </div>
          </div>

          <button type="submit" class="btn login-submit-btn w-100 mt-4" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            Se connecter
          </button>

          <p class="text-center text-muted small mt-3 mb-0">
            Vous êtes nouveau?
            <router-link to="/register" class="text-primary fw-semibold text-decoration-none">Créer Un Compte</router-link>
          </p>
        </form>

        <!-- Staff form -->
        <form v-else @submit.prevent="handleStaffLogin">
          <div class="mb-3">
            <label class="form-label fw-semibold text-dark small">Email</label>
            <input
              v-model="staffEmail"
              type="email"
              class="form-control login-input"
              placeholder="Email"
              required
            />
          </div>

          <div class="mb-2">
            <div class="d-flex justify-content-between align-items-center">
              <label class="form-label fw-semibold text-dark small mb-0">Mot de passe</label>
              <a href="#" class="text-primary small text-decoration-none">Mot de passe Oublié?</a>
            </div>
            <div class="position-relative mt-1">
              <input
                v-model="staffPassword"
                :type="showStaffPassword ? 'text' : 'password'"
                class="form-control login-input pe-5"
                placeholder="Password"
                required
              />
              <button
                type="button"
                class="btn-eye"
                @click="showStaffPassword = !showStaffPassword"
              >
                <i :class="showStaffPassword ? 'flaticon-eye-1' : 'flaticon-eye'" class="fs-14"></i>
              </button>
            </div>
          </div>

          <button type="submit" class="btn login-submit-btn w-100 mt-4" :disabled="staffLoading">
            <span v-if="staffLoading" class="spinner-border spinner-border-sm me-2"></span>
            Se connecter
          </button>
        </form>

      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { useRouter } from "vue-router";
import api from "@/utils/axios";
import { useAuthStore } from "@/stores/auth";
import { useAppToast } from "@/composables/useAppToast";

export default {
  name: "LoginForm",
  setup() {
    const router = useRouter();
    const { showToast } = useAppToast();

    const activeTab = ref("admin");

    // Admin
    const email = ref("");
    const password = ref("");
    const showPassword = ref(false);
    const loading = ref(false);

    // Staff
    const staffEmail = ref("");
    const staffPassword = ref("");
    const showStaffPassword = ref(false);
    const staffLoading = ref(false);

    // ── Admin login ─────────────────────────────────────────────────
    const handleLogin = async () => {
      loading.value = true;
      try {
        const auth = useAuthStore();
        const response = await api.post("/api/auth/client/login", {
          email: email.value,
          password: password.value,
        });

        if (response.data === "Unauthorized") {
          showToast("Email ou mot de passe incorrect", "error");
        } else if (response.data === "User Archived") {
          showToast("Utilisateur désactivé", "error");
        } else if (response.data === "not found") {
          showToast("Aucun compte trouvé avec cette adresse", "error");
        } else if (response.data.permissions && response.data.permissions.length === 0) {
          showToast("Aucune permission affectée à ce compte", "error");
        } else {
          auth.setToken(response.data.access_token);
          auth.setUser(response.data.user);
          auth.setPermissions(response.data.permissions);
          showToast("Connexion réussie", "success");
          router.replace("/").then(() => window.location.reload());
        }
      } catch (error) {
        console.error("Admin login error:", error.response?.data || error.message);
        showToast("Erreur serveur, réessayez plus tard", "error");
      } finally {
        loading.value = false;
      }
    };

    // ── Staff login ─────────────────────────────────────────────────
    const handleStaffLogin = async () => {
      staffLoading.value = true;
      try {
        const auth = useAuthStore();
        const response = await api.post("/api/auth/client/logins", {
          email: staffEmail.value,
          password: staffPassword.value,
        });

        const d = response.data;

        if (d === "Unauthorized" || d?.message === "Unauthorized") {
          showToast("Email ou mot de passe incorrect", "error");
        } else if (d === "User Archived" || d?.message === "User Archived") {
          showToast("Utilisateur désactivé", "error");
        } else if (d === "not found" || d?.message === "not found") {
          showToast("Aucun compte trouvé avec cette adresse", "error");
        } else {
          // Token: top-level or inside user
          const token = d.access_token ?? d.token ?? d.user?.token;
          if (!token) {
            showToast("Erreur d'authentification", "error");
            return;
          }

          const user = d.user ?? {};
          // Staff perms live in user.perms as {key: 0|1}
          const perms = user.perms ?? d.permissions ?? {};

          if (Object.keys(perms).length === 0) {
            showToast("Aucune permission affectée à ce compte", "error");
            return;
          }

          auth.setToken(token);
          auth.setUser(user);
          auth.setPermissions(perms);
          showToast("Connexion réussie", "success");
          router.replace("/").then(() => window.location.reload());
        }
      } catch (error) {
        console.error("Staff login error:", error.response?.data || error.message);
        showToast("Erreur serveur, réessayez plus tard", "error");
      } finally {
        staffLoading.value = false;
      }
    };

    return {
      activeTab,
      email, password, showPassword, loading, handleLogin,
      staffEmail, staffPassword, showStaffPassword, staffLoading, handleStaffLogin,
    };
  },
};
</script>

<style scoped>
/* ── Card ──────────────────────────────────────────────────────────── */
.login-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 32px rgba(0,0,0,.08);
  padding: 40px 36px;
  margin-top: 5rem;
}

/* ── Tab switcher ──────────────────────────────────────────────────── */
.login-tabs {
  display: flex;
  gap: 8px;
  background: #f1f3f5;
  border-radius: 10px;
  padding: 4px;
}
.login-tab-btn {
  flex: 1;
  border: none;
  background: transparent;
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s;
}
.login-tab-btn.active {
  background: #1b4ed8;
  color: #fff;
  box-shadow: 0 2px 8px rgba(27,78,216,.25);
}
.login-tab-btn:not(.active):hover {
  background: #e2e6ea;
  color: #343a40;
}

/* ── Input ─────────────────────────────────────────────────────────── */
.login-input {
  border-radius: 8px;
  border: 1px solid #dee2e6;
  padding: 10px 14px;
  font-size: 0.9rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.login-input:focus {
  border-color: #1b4ed8;
  box-shadow: 0 0 0 3px rgba(27,78,216,.12);
  outline: none;
}

/* ── Eye toggle ────────────────────────────────────────────────────── */
.btn-eye {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}
.btn-eye:hover { color: #343a40; }

/* ── Submit ────────────────────────────────────────────────────────── */
.login-submit-btn {
  background: #1b4ed8;
  color: #fff;
  border-radius: 8px;
  padding: 11px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  transition: background 0.2s, box-shadow 0.2s;
}
.login-submit-btn:hover:not(:disabled) {
  background: #1640b8;
  box-shadow: 0 4px 12px rgba(27,78,216,.3);
}
.login-submit-btn:disabled { opacity: 0.65; cursor: not-allowed; }
</style>
